import zod from 'zod';

// ! WARNING When adding new data types, must also update RecorderEquipmentTypes in python/libs/training_data/get_training_data_task_hardware_config.py
// ! WARNING If you create a new bot hardware with this equipment type, then consider how this affects users that are using older echo-bot or skills-bot builds.
export const BotHardwareEquipmentType = zod.enum([
  'robot',
  'gripper',
  'camera',
  'force_sensor',
  'depth_sensor',
]);
export type BotHardwareEquipmentType = zod.infer<
  typeof BotHardwareEquipmentType
>;

// ! WARNING When adding new data types, must also update RecorderDataType in python/libs/training_data/get_training_data_task_hardware_config.py
// ! WARNING If you create a new bot hardware with this data type, then consider how this affects users that are using older echo-bot or skills-bot builds.
export const BotHardwareDataType = zod.enum([
  'pose',
  'joint_states',
  'teleop_command',
  'gripper',
  'force_hardware',
  'video',
  'depth_image',
]);

export const BotHardware = zod.object({
  id: zod.string(),
  name: zod.string(),
  equipmentType: BotHardwareEquipmentType,
  dataType: BotHardwareDataType,
  metadata: zod.any().nullable(),
  archived: zod.boolean().default(false),
  created: zod.date().optional(),
});
export type BotHardware = zod.infer<typeof BotHardware>;

export const HardwareConfigSchema = zod.object({
  robots: zod.record(
    zod.string(),
    zod.object({
      isPrimary: zod.boolean(),
      hardware: zod.record(
        zod.string(),
        zod.object({
          id: zod.string(),
        }),
      ),
    }),
  ),
});
export type HardwareConfigSchema = zod.infer<typeof HardwareConfigSchema>;

export const HardwareConfigEquipmentMapping = zod.record(
  zod.string(),
  zod.object({
    robotId: zod.string().optional(), // only required for non-primary robots
    robotEquipmentId: zod.string().optional(), // only required for non-primary robots
    hardwareKeyToEquipmentId: zod.record(zod.string(), zod.string()),
  }),
);
export type HardwareConfigEquipmentMapping = zod.infer<
  typeof HardwareConfigEquipmentMapping
>;

export const HydratedHardwareConfigSchema = zod.object({
  robots: zod.record(
    zod.string(),
    zod.object({
      isPrimary: zod.boolean(),
      hardware: zod.record(zod.string(), BotHardware),
    }),
  ),
});
export type HydratedHardwareConfigSchema = zod.infer<
  typeof HydratedHardwareConfigSchema
>;

const ParsedHardwareConfigEntry = zod.object({
  id: zod.string(),
  name: zod.string(),
  config: HardwareConfigSchema,
});

const ParsedModelArchitecture = zod.object({
  id: zod.string(),
  name: zod.string(),
  modelType: zod.string(),
  inputHardwareConfigId: zod.string(),
  outputHardwareConfigId: zod.string(),
  inputHardwareConfig: ParsedHardwareConfigEntry.optional(),
  outputHardwareConfig: ParsedHardwareConfigEntry.optional(),
});

const ParsedModelMetadata = zod.object({
  id: zod.string(),
  created: zod.string(),
  finished: zod.string().nullable(),
  name: zod.string(),
  trainingDataProjectId: zod.string().nullable(),
  modelArchitecture: ParsedModelArchitecture,
  status: zod.string(),
  epoch: zod.number(),
  dockerImageTag: zod.string().nullable(),
  hyperparameters: zod.record(zod.string(), zod.any()).nullable(),
  provider: zod.string().nullable(),
  neptuneUrl: zod.string().nullable(),
  logUrl: zod.string().nullable(),
  notes: zod.string().nullable(),
  gpuCount: zod.number().nullable(),
  gpuType: zod.string().nullable(),
  costPerHour: zod.number().nullable(),
});

export const ParsedModelMetadataPayload = zod.object({
  trainingRun: ParsedModelMetadata.optional(),
  botHardware: zod.record(zod.string(), BotHardware),
});

export const ParsedModel = zod.object({
  modelFilenames: zod.array(zod.string()),
  modelMetadata: ParsedModelMetadataPayload.optional(),
  modelDirectory: zod.string(),
});

export type ParsedModel = zod.infer<typeof ParsedModel>;

// Processor Settings
export const ZarrSettings = zod.object({
  enabled: zod.boolean(),
  timestampSync: zod.boolean(),
});
export type ZarrSettings = zod.infer<typeof ZarrSettings>;

export const McapSettings = zod.object({
  enabled: zod.boolean(),
  timestampSync: zod.boolean(),
  exportUrdf: zod.boolean().nullable().optional().default(true),
});
export type McapSettings = zod.infer<typeof McapSettings>;

export const ProcessorSettings = zod.object({
  greenscreenProcessing: zod.boolean(),
  videoCompression: zod.boolean(),
  zarr: ZarrSettings,
  mcap: McapSettings,
});
export type ProcessorSettings = zod.infer<typeof ProcessorSettings>;

export const DEFAULT_PROCESSOR_SETTINGS: ProcessorSettings = {
  greenscreenProcessing: false,
  videoCompression: true,
  zarr: {
    enabled: true,
    timestampSync: true,
  },
  mcap: {
    enabled: true,
    timestampSync: false,
    exportUrdf: true,
  },
};
